import type { Breadcrumb, ErrorEvent } from "@sentry/react";

/**
 * Minimal local mirror of the @sentry/core `Log` interface (Sentry v10).
 * Mirrors era-api/src/lib/phiScrub.ts verbatim. @sentry/core is a transitive
 * dependency of @sentry/react and not directly resolvable under pnpm strict
 * hoisting, and @sentry/react does not re-export the `Log` type. This local
 * declaration matches the authoritative shape from
 * `@sentry/core/build/types/types-hoist/log.d.ts` for the fields beforeSendLog
 * is allowed to mutate (message, attributes).
 *
 * `message` is typed as plain `string`. The SDK's authoritative type is
 * `ParameterizedString` (a branded string carrying template-literal metadata),
 * but since `beforeSendLog` only needs to read and rewrite the message body
 * (no template-arg manipulation), plain `string` is sufficient — the SDK
 * accepts the rewritten value back via structural typing.
 * `attributes` is `Record<string, unknown>` per the SDK definition.
 */
interface Log {
	level: "trace" | "debug" | "info" | "warn" | "error" | "fatal";
	message: string;
	attributes?: Record<string, unknown>;
	severityNumber?: number;
}

/**
 * PHI denylist (CONTEXT.md C-04 / D-10).
 * Mirrored verbatim from era-api/src/instrument.ts (no shared workspace package in v1.11).
 *
 * Any object key matching this list has its value replaced with [REDACTED]
 * recursively — anywhere in the Sentry event payload tree.
 */
export const PHI_DENYLIST = [
	"title",
	"location",
	"notes",
	"medicationName",
	"doctorName",
	"notificationBody",
] as const;

const PHI_DENYLIST_SET: ReadonlySet<string> = new Set(PHI_DENYLIST);

const REDACTED = "[REDACTED]";

// Catches interpolated PHI in free-form strings.
// Handles three call-site shapes:
//   medicationName: 'Cetrotide'        (object literal / log line)
//   medicationName="Cetrotide"          (HTML/attr-style)
//   "medicationName": "Cetrotide"       (JSON.stringify output — note the closing quote
//                                        between key and colon, which is why we allow
//                                        an optional `"` before the separator)
const PHI_KEYS_PATTERN = PHI_DENYLIST.join("|");
const PHI_MESSAGE_REGEX = new RegExp(
	`(\\b(?:${PHI_KEYS_PATTERN})\\b"?\\s*[:=]\\s*)('[^']*'|"[^"]*"|\\S+)`,
	"gi",
);

/**
 * Recursively walk a value and replace any property whose key is in PHI_DENYLIST
 * with REDACTED. Pure — does not mutate the input.
 *
 * Cycle-safe via a WeakSet. There is no fixed depth limit — PHI nested arbitrarily
 * deep in a Sentry event will still be redacted. Cycles short-circuit to '[Circular]'
 * so we cannot get stuck on self-referential structures.
 *
 * Module has NO Sentry runtime import path (only `import type`) and no Vite globals,
 * so it is safe to load from tsx scripts, Jest, Vitest, browser, or Node.
 */
export function redactPhi<T>(input: unknown): T {
	return redactInternal(input, new WeakSet()) as T;
}

function redactInternal(input: unknown, seen: WeakSet<object>): unknown {
	if (input === null || input === undefined) return input;
	if (typeof input !== "object") return input;
	if (seen.has(input as object)) return "[Circular]";
	seen.add(input as object);

	if (Array.isArray(input)) {
		return input.map((v) => redactInternal(v, seen));
	}

	const out: Record<string, unknown> = {};
	for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
		out[k] = PHI_DENYLIST_SET.has(k) ? REDACTED : redactInternal(v, seen);
	}
	return out;
}

/**
 * Apply key-name PHI redaction to free-form strings (event.message,
 * event.exception.values[].value, breadcrumb.message).
 *
 * Examples caught:
 *   `Failed to update: medicationName='Cetrotide'`
 *   `payload: {"medicationName":"Cetrotide"}`
 *   `<input notificationBody="Take your dose">`
 */
export function redactMessageString(
	msg: string | undefined,
): string | undefined {
	if (!msg) return msg;
	// Reset the lastIndex on the cached global regex before each call to avoid
	// stateful leakage between invocations.
	PHI_MESSAGE_REGEX.lastIndex = 0;
	return msg.replace(PHI_MESSAGE_REGEX, `$1${REDACTED}`);
}

/**
 * Sentry beforeSend callback: applies redactPhi to structured event fields,
 * strips user PII, and runs the message regex over free-form strings.
 *
 * Mutates the event in place per Sentry's beforeSend contract (the SDK owns
 * the event lifecycle).
 */
export function scrubbedBeforeSend(event: ErrorEvent): ErrorEvent | null {
	if (event.user) {
		event.user.email = undefined;
		event.user.ip_address = undefined;
		event.user.username = undefined;
	}
	if (event.extra) event.extra = redactPhi(event.extra);
	if (event.contexts) event.contexts = redactPhi(event.contexts);
	if (Array.isArray(event.breadcrumbs)) {
		event.breadcrumbs = event.breadcrumbs.map((b) => ({
			...b,
			message: redactMessageString(b.message),
			data: b.data ? redactPhi<typeof b.data>(b.data) : b.data,
		}));
	}
	if (event.request?.data) {
		event.request.data = redactPhi(event.request.data);
	}
	if (event.message) {
		event.message = redactMessageString(event.message);
	}
	if (event.exception?.values) {
		event.exception.values = event.exception.values.map((v) => ({
			...v,
			value: redactMessageString(v.value),
		}));
	}
	return event;
}

/**
 * Sentry beforeBreadcrumb callback: redact PHI keys recursively from
 * `breadcrumb.data` AND apply the message regex to `breadcrumb.message`.
 * The message arm catches `console.log("medicationName=Cetrotide")` which
 * Sentry auto-captures into a breadcrumb whose PHI lives in the message
 * field, not in data.
 */
export function scrubbedBeforeBreadcrumb(
	breadcrumb: Breadcrumb,
): Breadcrumb | null {
	const next: Breadcrumb = { ...breadcrumb };
	if (breadcrumb.data) next.data = redactPhi(breadcrumb.data);
	if (breadcrumb.message)
		next.message = redactMessageString(breadcrumb.message);
	return next;
}

/**
 * Sentry beforeSendLog callback (Sentry v10+ structured Logs API).
 *
 * Mitigates T-34.1-PHI-LOGS: the Logs pipeline bypasses beforeSend entirely,
 * so PHI in console.log() calls (auto-instrumented into log entries) would
 * reach Sentry Logs unscrubbed without this callback.
 *
 * Touches only log.message and log.attributes — the serialized `body` field
 * does NOT exist on the Log type passed to beforeSendLog (it appears only on
 * SerializedLog, which beforeSendLog never sees).
 *
 * Mutates the log in place per Sentry's contract.
 */
export function scrubbedBeforeSendLog(log: Log): Log | null {
	if (log.message) {
		// ParameterizedString brand preserved via cast at the assignment site.
		log.message = redactMessageString(log.message) as typeof log.message;
	}
	if (log.attributes) {
		log.attributes = redactPhi(log.attributes) as typeof log.attributes;
	}
	return log;
}
